import { FormControl, Text } from '@chakra-ui/react';
import type { UTCDate } from '@date-fns/utc';
import classNames from 'classnames';
import { clamp } from 'date-fns';
import mixpanel from 'mixpanel-browser';
import { useCallback, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import type { SingleValue } from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';

import { AiSearchTrackingEvents } from '@revelio/core';
import { Input, Select } from '@revelio/ui';

import { SelectionListControls } from '../../../../../shared/selection-lists/components/SelectionListControls/SelectionListControls';
import { useTabContentConfig } from '../../../components/PageConfigProvider/PageConfigProvider';
import { type DateOption, DatePresets } from '../../constants/datePresets';
import { useAvailableDateRangeWithTrialLag } from '../../hooks/useAvailableDateRangeWithTrialLag';
import { useFilterState } from '../../hooks/useFilters';
import { useTrackFilterReset } from '../../hooks/useTrackFilterReset';
import { useViewDateGranularity } from '../../hooks/useViewDateGranularity';
import { type DateFilter, FilterId, FilterType } from '../../types/filters';
import { localToUTC, utcToLocal } from '../../utils/dateUtils';
import overlaySelectStyles from '../filter-overlay-select.module.css';
import styles from './date-filter-form.module.css';
import { computeDateFromOption, identifyDatePreset } from './utils';

interface DateFilterFormProps {
  closeMenu: () => void;
}

function DateFilterForm({ closeMenu }: DateFilterFormProps) {
  const { availableDateRange } = useAvailableDateRangeWithTrialLag();
  const { revelioPageId } = useTabContentConfig();
  const {
    filterState: snapshotDateFilter,
    updateFilter: updateSnapshotDateFilter,
  } = useFilterState<DateFilter>(FilterId.SNAPSHOT_DATE);

  const viewGranularity = useViewDateGranularity();
  const trackFilterReset = useTrackFilterReset();

  const [selectedOption, setSelectedOption] = useState<DateOption | null>(null);
  const [localDate, setLocalDate] = useState<UTCDate | null>(null);
  const [error, setError] = useState<string>('');

  const systemMinDate =
    viewGranularity === 'month'
      ? (availableDateRange?.month.min ?? null)
      : (availableDateRange?.week.min ?? null);
  const systemMaxDate =
    viewGranularity === 'month'
      ? (availableDateRange?.month.max ?? null)
      : (availableDateRange?.week.max ?? null);

  const isLoading = !availableDateRange;

  useEffect(() => {
    if (isLoading || !systemMinDate || !systemMaxDate) return;

    const displayStartDate = snapshotDateFilter?.value
      ? (clamp(snapshotDateFilter.value, {
          start: systemMinDate,
          end: systemMaxDate,
        }) as UTCDate)
      : null;

    const presetKey = identifyDatePreset(
      snapshotDateFilter,
      viewGranularity,
      availableDateRange
    );

    const foundOption =
      DatePresets.find((o: DateOption) => o.value === presetKey) || null;

    setSelectedOption(foundOption);
    setLocalDate(displayStartDate);
    setError('');
  }, [
    snapshotDateFilter,
    isLoading,
    systemMinDate,
    systemMaxDate,
    viewGranularity,
    availableDateRange,
  ]);

  const updateLocalDates = useCallback(
    (option: DateOption) => {
      setSelectedOption(option);
      setError('');

      if (isLoading || !systemMinDate || !systemMaxDate) {
        setLocalDate(null);
        return;
      }

      if (option.value === 'max') {
        setLocalDate(systemMaxDate);
      } else if (option.value === 'custom') {
        setLocalDate(snapshotDateFilter?.value ?? localDate);
      } else {
        const computed = computeDateFromOption(
          option,
          availableDateRange,
          viewGranularity
        );
        if (computed) {
          setLocalDate(computed);
        } else {
          setLocalDate(availableDateRange[viewGranularity].max);
        }
      }
    },
    [
      isLoading,
      systemMinDate,
      systemMaxDate,
      snapshotDateFilter?.value,
      localDate,
      availableDateRange,
      viewGranularity,
    ]
  );

  const handleOptionChange = useCallback(
    (newOption: SingleValue<DateOption>) => {
      if (!newOption) return;
      updateLocalDates(newOption);
    },
    [updateLocalDates]
  );

  const handleApply = () => {
    if (isLoading || (selectedOption?.value === 'custom' && error)) return;
    if (!selectedOption && !localDate) return;

    const snapshotDateValue = localDate ? localToUTC(localDate) : null;

    updateSnapshotDateFilter({
      ...snapshotDateFilter,
      id: FilterId.SNAPSHOT_DATE,
      value: snapshotDateValue,
    });
    // Same shape as the other manual filter forms; the preset (not the date
    // value) keeps this low-cardinality.
    mixpanel.track(AiSearchTrackingEvents.FILTERS_APPLIED, {
      method: 'manual',
      filter_id: FilterId.SNAPSHOT_DATE,
      filter_type: FilterType.DATE,
      date_preset: selectedOption?.value,
      page_id: revelioPageId,
    });
    closeMenu();
  };

  const handleClear = () => {
    trackFilterReset(
      { id: FilterId.SNAPSHOT_DATE, type: FilterType.DATE },
      { selectionCountBefore: localDate ? 1 : 0 }
    );
    setError('');
    updateSnapshotDateFilter({
      id: FilterId.SNAPSHOT_DATE,
      value: null,
    });
    closeMenu();
  };

  const isCustomSelected = selectedOption?.value === 'custom';

  const commonPickerProps = (() => {
    const base = {
      minDate: systemMinDate ?? undefined,
      maxDate: systemMaxDate ?? undefined,
      placeholderText: viewGranularity === 'week' ? 'YYYY-MM-DD' : 'YYYY-MM',
    };

    if (viewGranularity === 'month') {
      return {
        ...base,
        showMonthYearPicker: true,
        dateFormat: 'yyyy-MM',
        showMonthDropdown: false,
        showYearDropdown: true,
        dropdownMode: 'select',
        yearDropdownItemNumber: 15,
        scrollableYearDropdown: true,
      } as const;
    }

    return {
      ...base,
      showWeekPicker: true,
      showWeekNumbers: true,
      dateFormat: 'yyyy-MM-dd',
      showMonthDropdown: true,
      showYearDropdown: true,
      dropdownMode: 'select',
      yearDropdownItemNumber: 15,
      scrollableYearDropdown: true,
    } as const;
  })();

  const renderDateInput = (
    label: string,
    selected: UTCDate | null,
    onChange: (date: UTCDate | null) => void,
    pickerMinDate: UTCDate | null,
    pickerMaxDate: UTCDate | null
  ) => (
    <FormControl flex={1} isDisabled={isLoading}>
      <Text fontSize="sm" pb={1}>
        {label}
      </Text>
      <DatePicker
        selected={selected ? utcToLocal(selected) : null}
        onChange={(date) => onChange(localToUTC(date))}
        {...commonPickerProps}
        yearDropdownItemNumber={15}
        scrollableYearDropdown
        showMonthYearPicker={true}
        showYearDropdown={false}
        dropdownMode="select"
        customInput={
          <Input
            data-testid="custom-date"
            size="sm"
            wrapperClassName={styles.dateInput}
          />
        }
        disabled={isLoading}
        minDate={pickerMinDate ? utcToLocal(pickerMinDate) : null}
        maxDate={pickerMaxDate ? utcToLocal(pickerMaxDate) : null}
        onBlur={(e) => {
          e.preventDefault();
        }}
        popperClassName="permanent-inline-date-select-popper"
      />
    </FormControl>
  );

  return (
    <div className={styles.form}>
      <FormControl>
        <Select<DateOption, false>
          options={DatePresets}
          value={selectedOption}
          onChange={handleOptionChange}
          size="sm"
          isDisabled={isLoading}
          id="filter-range"
          isSearchable={false}
          className={classNames(
            overlaySelectStyles.overlaySelect,
            overlaySelectStyles.operatorSelect
          )}
          classNamePrefix="filter-overlay-select"
        />
      </FormControl>

      {isCustomSelected &&
        renderDateInput(
          'Date',
          localDate,
          setLocalDate,
          systemMinDate ?? null,
          systemMaxDate ?? null
        )}

      {error && (
        <Text fontSize="sm" color="red.500">
          {error}
        </Text>
      )}

      <SelectionListControls
        onClear={handleClear}
        onSubmit={handleApply}
        onClose={closeMenu}
        clearText="Clear"
        submitText="Apply"
        isSubmitDisabled={!!error || isLoading}
      />
    </div>
  );
}

export default DateFilterForm;
